import Decimal from 'decimal.js';
import { AppliedAdditionalCharge, TaxLine } from 'store/invoices/types';
import { getIrelandTaxCodePercents } from 'store/invoice-config/types';

type MoneyInstance = InstanceType<typeof Money>;
type DecimalValue = Decimal.Value;
type InputValue = DecimalValue | null | undefined;

const Money = Decimal.clone({
    precision: 40,
    rounding: Decimal.ROUND_HALF_UP,
});

const D = (v: InputValue) => new Money(v ?? 0);
const toDP2 = (x: MoneyInstance) => x.toDecimalPlaces(2, Money.ROUND_HALF_UP);
const sum = (vals: MoneyInstance[]) => vals.reduce((a, b) => a.plus(b), D(0));
const amount = (v: string | number | null | undefined) => D(v ?? 0);

export const getEffectiveUnitPrice = (charge: AppliedAdditionalCharge, liabilityGross: number): number => {
    const fixedPrice = D(charge.amount ?? 0);
    if (charge.percentage == null) return fixedPrice.toNumber();
    const percentagePrice = D(liabilityGross).times(D(charge.percentage).div(100));
    return Money.max(fixedPrice, percentagePrice).toNumber();
};

export const InvoiceUtils = {
    calculateTotals(
        taxLines: TaxLine[] | null | undefined,
        additionalCharges: AppliedAdditionalCharge[] | null | undefined
    ): { net: number; tax: number; gross: number; liabilityGross: number } {
        const liabilityNet = sum((taxLines ?? []).map((l) => amount(l.netAmount)));
        const liabilityTax = sum((taxLines ?? []).map((l) => amount(l.taxAmount)));
        const liabilityGross = toDP2(liabilityNet.plus(liabilityTax)).toNumber();
        const activeCharges = additionalCharges?.filter((c) => c.active) ?? [];

        const chargesNet = sum(
            activeCharges.map((c) => D(getEffectiveUnitPrice(c, liabilityGross)).times(D(c.quantity ?? 1)))
        );

        const chargesTax = sum(
            activeCharges.map((c) => {
                const net = D(getEffectiveUnitPrice(c, liabilityGross)).times(D(c.quantity ?? 1));
                const rate = D(getIrelandTaxCodePercents(c.taxCode)).div(100);
                return net.times(rate);
            })
        );

        const net = toDP2(liabilityNet.plus(chargesNet)).toNumber();
        const tax = toDP2(liabilityTax.plus(chargesTax)).toNumber();
        const gross = toDP2(D(net).plus(tax)).toNumber();
        return { net, tax, gross, liabilityGross };
    },

    getRemainingPayment(totalAmount: number | null | undefined, amountPaid: number | null | undefined): number {
        return toDP2(D(totalAmount).minus(D(amountPaid))).toNumber();
    },
};
